import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QrCode, User, UserPlus, ChevronRight, AlertCircle, CheckCircle2, Globe, LogOut } from "lucide-react";
import PageSpinner from "@/components/PageSpinner";
import { useAuth } from "@/lib/AuthContext";
import { useQRScan, useClaimProduct, useQRCheck } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import CtaLink from "@/components/CtaLink";
import { withCta } from "@/lib/ctaTracking";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/components/LanguageContext";
import { apiMessage } from "@/api/apiMessage";

export default function Redirect() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const qrId = searchParams.get('id');
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const scanMutation = useQRScan();
  const claimMutation = useClaimProduct();

  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);
  const { data: checkData } = useQRCheck(qrId);

  useEffect(() => {
    if (!qrId) {
      setError(t('redirect.device_not_specified'));
      return;
    }

    const performScan = async () => {
      try {
        const result = await scanMutation.mutateAsync({ qrId });
        if (result.registered) {
          if (result.redirectUrl) {
            // Si el destino es una ruta interna de la propia SPA (p.ej. el /en1/
            // de un todo_en_1), navegamos client-side para NO recargar la página
            // entera: una recarga rearranca la SPA y muestra un segundo spinner
            // distinto. Las URLs externas (Google Reviews, Instagram…) sí usan
            // redirect duro.
            try {
              const target = new URL(result.redirectUrl, window.location.origin);
              if (target.origin === window.location.origin) {
                navigate(target.pathname + target.search, { replace: true });
                return;
              }
            } catch { /* URL malformada → cae al redirect duro de abajo */ }
            window.location.href = result.redirectUrl;
          } else {
            // Si está registrado pero NO tiene URL (común en URL/Custom nuevos)
            setScanResult(result);
            // El renderizado de abajo manejará si somos dueños o no
          }
        } else {
          // Si NO está registrado, mostrar la UI de Reclamar
          setScanResult(result);
        }
      } catch (err) {
        console.error("Scan error:", err);
        setError(apiMessage(err, t, t('redirect.scan_error')));
      }
    };

    performScan();
  }, [qrId, t]);

  // Si está registrado pero no hay URL, y checkData ya cargó
  useEffect(() => {
    if (scanResult?.registered && !scanResult?.redirectUrl && checkData) {
      if (checkData.isOwner) {
        // Si soy el dueño, llevar a configuración inmediatamente
        const pType = checkData.product?.platform;
        if (pType === 'todo_en_1' || pType === 'tipo_todo_en_1') {
          navigate(`/LinkTreeConfig?productId=${checkData.product.id}`);
        } else {
          navigate(`/RegisterQR?qrId=${qrId}`);
        }
      }
    }
  }, [scanResult, checkData, qrId, navigate]);

  const handleStartConfig = () => {
    if (!isAuthenticated) {
      // Usar pathname + search para volver exactamente a esta pantalla de escaneo
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    
    const pType = checkData?.product?.platform || 'resenas';
    if (pType === 'todo_en_1' || pType === 'tipo_todo_en_1') {
      navigate(`/LinkTreeConfig?qrId=${qrId}`);
    } else {
      navigate(`/RegisterQR?qrId=${qrId}`);
    }
  };

  const handleLogoutAndSwitch = async () => {
    await logout(false); // logout without redirect to /login
    navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
  };

  // State: Loading — usa el MISMO spinner global que el app-shell y LinkTree,
  // para que el cliente no perciba un cambio de un spinner a otro al encadenar
  // scan → /en1/ (mismo flujo de escaneo, un único loader consistente).
  if (!scanResult && !error) {
    return <PageSpinner />;
  }

  // State: Error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-none shadow-2xl rounded-3xl overflow-hidden">
          <div className="h-2 bg-red-500" />
          <CardContent className="pt-10 pb-8 text-center space-y-4">
            <div className="mx-auto w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-2">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 leading-tight">{t('redirect.oops')}</h1>
            <p className="text-slate-600 font-medium px-4">{error}</p>
            <div className="pt-4">
              <p className="text-xs text-slate-400">{t('redirect.contact_owner')}</p>
            </div>
            <Button
              variant="outline"
              className="w-full h-12 rounded-xl mt-4 border-slate-200"
              onClick={() => window.location.reload()}
            >
              {t('redirect.retry_scan')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  // State: Unregistered (Redesigned)
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#f8fafc_0%,_#f1f5f9_100%)] flex flex-col items-center justify-center p-6 sm:p-8 font-sans">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-40">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-[#feae2c]/10 rounded-full blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] w-[35%] h-[35%] bg-blue-100/50 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-2xl w-full flex flex-col items-center">
        {/* Header / Logo */}
        <div className="mb-10 animate-in fill-mode-both ease-out fade-in slide-in-from-top-5 duration-700">
          {/* <img src="/logo.png" alt="Tapstar Logo" className="h-10 md:h-12 mx-auto grayscale opacity-90 hover:grayscale-0 transition-all duration-500 cursor-default" /> */}
        </div>

        <div className="w-full animate-in fill-mode-both ease-out fade-in slide-in-from-bottom-8 duration-700 delay-100">
          <Card className="border-none shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pt-12 pb-2">
              <div className="mx-auto mb-6 w-16 h-16 bg-[#feae2c]/10 rounded-2xl flex items-center justify-center animate-in fill-mode-both ease-out fade-in zoom-in-90 duration-300 delay-300">
                <QrCode className="w-8 h-8 text-[#feae2c]" />
              </div>
              <CardTitle className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
                {scanResult?.registered ? t('redirect.device_found') : t('redirect.connect_device')}
              </CardTitle>
              <CardDescription className="text-slate-500 text-lg max-w-sm mx-auto pt-4 leading-relaxed font-medium">
                {!isAuthenticated
                  ? t('redirect.register_to_use')
                  : scanResult?.registered
                    ? t('redirect.owned_no_config')
                    : t('redirect.connect_to_config')}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-10 px-8 md:px-14 pb-14">
              {/* ID Badge removed as requested */}

              {/* Action Box — solo visible si el usuario ya tiene sesión */}
              {isAuthenticated && (
                <div className="relative pt-4">
                  <div className="relative bg-slate-50/50 border border-slate-100 p-8 sm:p-10 rounded-[2rem] text-center space-y-8">
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-slate-800">{t('redirect.manage_device')}</h3>
                      {/* Píldora de sesión: apilada (etiqueta + email en líneas
                          separadas) y con break-all + max-w-full para que un
                          correo largo no desborde la tarjeta en móvil. */}
                      <div className="inline-flex max-w-full flex-col items-center gap-1 px-4 py-2 bg-green-50 rounded-2xl border border-green-100 shadow-sm">
                        <span className="flex items-center gap-2 text-xs text-green-700 font-semibold uppercase tracking-wider">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse shrink-0" />
                          {t('redirect.active_session')}
                        </span>
                        <span className="max-w-full break-all text-xs font-semibold text-green-800">{user?.email}</span>
                      </div>
                    </div>

                    <div className="transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]">
                      {scanResult?.registered ? (
                        <div className="p-4 bg-slate-100 rounded-2xl">
                           <p className="text-slate-600 font-medium">
                             {t('redirect.contact_owner_config')}
                           </p>
                        </div>
                      ) : (
                        <Button
                          className="w-full min-h-[4rem] h-auto py-3 bg-[#feae2c] hover:bg-[#ff9d00] text-white font-extrabold text-lg sm:text-xl rounded-2xl shadow-[0_10px_30px_rgba(254,174,44,0.3)] hover:shadow-[0_15px_40px_rgba(254,174,44,0.4)] transition-all duration-300 flex items-center justify-center gap-3 whitespace-normal text-center leading-tight"
                          onClick={handleStartConfig}
                        >
                          <CheckCircle2 className="w-7 h-7" />
                          <span>{t('redirect.config_now')}</span>
                        </Button>
                      )}
                    </div>

                    <button
                      onClick={handleLogoutAndSwitch}
                      className="text-sm text-slate-400 hover:text-[#feae2c] transition-all flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-center mx-auto font-medium py-2 px-4 rounded-lg hover:bg-slate-100/50"
                    >
                      {t('redirect.not_your_account')} <span className="underline decoration-slate-200 hover:decoration-[#feae2c]">{t('redirect.switch_user')}</span>
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Actions - Modern integrated design */}
        <div className="mt-12 w-full grid grid-cols-1 md:grid-cols-2 gap-8 px-2 sm:px-0">
          {isAuthenticated ? (
            <>
              {/* Card 1: Account (When authenticated, goes first) */}
              <div className="animate-in fill-mode-both ease-out fade-in slide-in-from-left-8 duration-700 delay-300">
                <div className="group h-full p-8 rounded-[2rem] bg-white border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.06)] transition-all duration-500 overflow-hidden relative">
                  <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                    <User className="w-24 h-24 text-slate-900" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                      <LogOut className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-slate-800 mb-2">{t('redirect.use_another_account')}</h4>
                    <p className="text-sm text-slate-500 mb-8 flex-grow">{t('redirect.change_session_desc')}</p>
                    <Button
                      variant="outline"
                      className="w-full min-h-[3rem] h-auto py-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-100 rounded-xl font-bold transition-all whitespace-normal text-center leading-tight"
                      onClick={handleLogoutAndSwitch}
                    >
                      {t('redirect.enter_other_profile')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Card 2: Register */}
              <div className="animate-in fill-mode-both ease-out fade-in slide-in-from-right-8 duration-700 delay-500">
                <div className="group h-full p-8 rounded-[2rem] bg-[#feae2c] shadow-[0_12px_40px_rgba(254,174,44,0.2)] hover:shadow-[0_20px_50px_rgba(254,174,44,0.3)] transition-all duration-500 overflow-hidden relative border border-[#feae2c]/20">
                  <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                    <UserPlus className="w-24 h-24 text-white" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="w-12 h-12 bg-white/20 text-white rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                      <UserPlus className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2">{t('redirect.new_to_tapstar')}</h4>
                    <p className="text-sm text-white/80 mb-8 flex-grow">{t('redirect.create_free_account_desc')}</p>
                    <CtaLink
                      as={Button}
                      cta="redirect.nuevo.crear-cuenta"
                      className="w-full min-h-[3rem] h-auto py-2 bg-white text-[#feae2c] hover:bg-white/90 rounded-xl font-bold transition-all shadow-lg whitespace-normal text-center leading-tight"
                      onClick={() => navigate(withCta(`/register?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, 'redirect.nuevo.crear-cuenta'))}
                    >
                      {t('redirect.create_account_btn')}
                    </CtaLink>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Card 1: Register (When NOT authenticated, goes first) */}
              <div className="animate-in fill-mode-both ease-out fade-in slide-in-from-left-8 duration-700 delay-300">
                <div className="group h-full p-8 rounded-[2rem] bg-[#feae2c] shadow-[0_12px_40px_rgba(254,174,44,0.2)] hover:shadow-[0_20px_50px_rgba(254,174,44,0.3)] transition-all duration-500 overflow-hidden relative border border-[#feae2c]/20">
                  <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                    <UserPlus className="w-24 h-24 text-white" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="w-12 h-12 bg-white/20 text-white rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                      <UserPlus className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2">{t('redirect.new_to_tapstar')}</h4>
                    <p className="text-sm text-white/80 mb-8 flex-grow">{t('redirect.create_free_account_desc')}</p>
                    <CtaLink
                      as={Button}
                      cta="redirect.nuevo.crear-cuenta"
                      className="w-full min-h-[3rem] h-auto py-2 bg-white text-[#feae2c] hover:bg-white/90 rounded-xl font-bold transition-all shadow-lg whitespace-normal text-center leading-tight"
                      onClick={() => navigate(withCta(`/register?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, 'redirect.nuevo.crear-cuenta'))}
                    >
                      {t('redirect.create_account_btn')}
                    </CtaLink>
                  </div>
                </div>
              </div>

              {/* Card 2: Account (Reads as Iniciar Sesion) */}
              <div className="animate-in fill-mode-both ease-out fade-in slide-in-from-right-8 duration-700 delay-500">
                <div className="group h-full p-8 rounded-[2rem] bg-white border border-slate-100 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.06)] transition-all duration-500 overflow-hidden relative">
                  <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                    <User className="w-24 h-24 text-slate-900" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                      <LogOut className="w-6 h-6" />
                    </div>
                    <h4 className="text-lg font-bold text-slate-800 mb-2">{t('login_button')}</h4>
                    <p className="text-sm text-slate-500 mb-8 flex-grow">{t('redirect.login_desc')}</p>
                    <Button
                      variant="outline"
                      className="w-full min-h-[3rem] h-auto py-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-100 rounded-xl font-bold transition-all whitespace-normal text-center leading-tight"
                      onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                    >
                      {t('login_button').toUpperCase()}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer info - Subtle & Minimalist */}
        <div className="mt-16 sm:mt-20 text-slate-400 text-xs flex flex-col items-center gap-4 animate-in fill-mode-both ease-out fade-in duration-300 delay-700">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 opacity-60"><Globe className="w-3.5 h-3.5" /> {t('redirect.encrypted_connection')}</span>
            <span className="w-1 h-1 bg-slate-300 rounded-full" />
            <span className="opacity-60">Tapstar.</span>
          </div>
          <div className="flex gap-6 opacity-40 hover:opacity-100 transition-opacity">
            <a href="/terms" className="hover:text-slate-600 transition-colors">{t('termsTitle')}</a>
            <a href="/privacy" className="hover:text-slate-600 transition-colors">{t('privacyTitle')}</a>
            <a href="https://mytapstar.com" className="hover:text-slate-600 transition-colors">{t('redirect.official_web')}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
